import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { signalrService } from "../services";
import { SignalRActionName, SignalRActionPayload, SignalRMethodName, SignalRPayload } from "../types/signalr";
import { toast } from "vue3-toastify";
import { useLangStore } from "./lang";
import { TEXT } from "../configs/text";
import { useClassStore } from "./class";
import { UserRole } from "../types/user";
import { ClassStatus, ClassViewMode, StudentChangeGroup } from "../types/class";
import { HubConnectionState } from "@microsoft/signalr";
import { useUserStore } from "./user";
import { useAuthStore } from "./auth";
import { useLoadingStore } from "./loading";
import { Session } from "../types/session";
import { useSessionStore } from "./session";
import { useQuizStore } from "./quiz";
import { AssessmentType } from "../types/assessment";
import { QuizCurrentQuestion, QuizState } from "../types/quiz";
import { useLectureStore } from "./lecture";
// import * as Sentry from '@sentry/vue'

export const useSignalRStore = defineStore('signalr', () => {
    const lang = useLangStore()
    const classStore = useClassStore()
    const user = useUserStore()
    const sessionStore = useSessionStore()
    const quizStore = useQuizStore()
    const lecture = useLectureStore()

    const isConnected = ref<boolean>(false)
    // const isJoinedRoom = ref<boolean>(false)

    watch([() => classStore.roomName, isConnected], ([newRoomName], [prevRoomName]) => {
        if (isConnected && !newRoomName) {
            // leaveRoom(prevRoomName)
            console.log('prevRoomName', prevRoomName)
            return;
        }
        if (isConnected && newRoomName) {
            joinRoom(newRoomName)
            return;
        }
    })

    async function connect() {
        const connectionState = await signalrService.connect()
        isConnected.value = connectionState === HubConnectionState.Connected ? true : false
    }

    function disconnect() {
        signalrService.disconnect()
        isConnected.value = false
    }

    function onEventHandler() {
        signalrService.connection.on(SignalRMethodName.received, methodHandler)
    }

    function offEventHandler() {
        signalrService.connection.off(SignalRMethodName.received, methodHandler)
    }

    async function getCurrentQuestion(): Promise<QuizCurrentQuestion | undefined> {
        return await signalrService.getCurrentQuestion(classStore.roomName);
    }

    function joinRoom(roomName: string) {
        const roomId = classStore.selectedClassDetails?.id
        if (!roomName || !roomId) return;

        signalrService.joinRoom(roomName, roomId)
    }

    function leaveRoom(roomName: string) {
        if (!roomName) return;
        signalrService.leaveRoom(roomName)
    }

    function studentChooseGroup(groupId: number) {
        signalrService.studentChooseGroup(classStore.roomName, groupId)
    }

    function teacherChangeGroup(studentChangeGroups: StudentChangeGroup[], approve: boolean) {
        studentChangeGroups.forEach(async studentChangeGroup => {
            if (approve) {
                if (studentChangeGroup.groupId && classStore.selectedClassDetails?.id) {
                    await signalrService.approveJoin(studentChangeGroup.username, classStore.roomName)
                    signalrService.teacherChangeGroup(classStore.roomName, classStore.selectedClassDetails.id, studentChangeGroup.groupId, studentChangeGroup.studentId)
                }
                return
            }
            signalrService.teacherChangeGroup(classStore.roomName, classStore.selectedClassDetails?.id || 0, studentChangeGroup.groupId || 0, studentChangeGroup.studentId)
        })
    }

    function studentAskJoinRoom() {
        if (user.userRole !== UserRole.student) return;

        signalrService.studentAskJoinRoom(classStore.roomName)
    }

    function startClassRoom() {
        if (user.userRole !== UserRole.teacher) return;
        if (!classStore.selectedClassDetails?.id) return;

        signalrService.startClassRoom(classStore.roomName, classStore.selectedClassDetails.id)
    }

    function endClassRoom() {
        if (user.userRole !== UserRole.teacher) return;
        if (!classStore.selectedClassDetails?.id) return;

        signalrService.endClassRoom(classStore.roomName, classStore.selectedClassDetails.id)
    }

    function allowRaiseHand() {
        if (user.userRole !== UserRole.teacher) return;

        signalrService.requestRaiseHand(classStore.roomName, true)
    }

    function disableRaiseHand() {
        if (user.userRole !== UserRole.teacher) return;

        signalrService.requestRaiseHand(classStore.roomName, false)
    }

    function removeRequestRaiseHand() {
        if (user.userRole !== UserRole.teacher) return;

        const usernames = classStore.raiseHandStudentIds.map(studentId => {
            const student = classStore.selectedClassDetails?.students?.find(student => student.id === studentId)
            if (!student) return '';
            return student.username
        })
        signalrService.removeRequestRaiseHand(classStore.roomName, usernames)
    }

    function raiseHand() {
        if (user.userRole !== UserRole.student) return;

        signalrService.raiseHand(classStore.roomName)
    }

    function removeRaiseHand() {
        if (user.userRole !== UserRole.student) return;

        signalrService.removeRaiseHand(classStore.roomName)
    }

    function lockScreen(status: boolean) {
        if (user.userRole !== UserRole.teacher) return;

        signalrService.lockScreen(classStore.roomName, status)
    }

    function rewardPointStudent(studentId: number, point: number, session: Session, id: number | null) {
        if (user.userRole !== UserRole.teacher) return;

        const student = classStore.selectedClassDetails?.students?.find(student => student.id === studentId)
        if (!student) return;

        signalrService.rewardPoint(classStore.roomName, student.username, point, session, id)
    }

    function rewardPointGroup(groupId: number, point: number, session: Session, id: number | null) {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.rewardPointGroup(classStore.roomName, groupId, point, session, id)
    }

    function forceScreen() {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.controlScreen(classStore.roomName, sessionStore.currentSession)
    }

    function unForceScreen() {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.controlScreen(classStore.roomName, null)
    }

    function startPractice() {
        if (user.userRole !== UserRole.teacher) return;

        const practiceState = sessionStore.practiceQuizState
        signalrService.setPracticeState(classStore.roomName, {
            ...practiceState,
            [sessionStore.currentSession]: true
        })
    }

    function updateQuizUrl(url: string) {
        if (user.userRole !== UserRole.teacher) return;
        if (!classStore.selectedClassDetails?.id) return;

        signalrService.updateQuizUrl(classStore.roomName, classStore.selectedClassDetails.id, url)
    }

    function startQuiz() {
        if (user.userRole !== UserRole.teacher) return;
        const practiceState = sessionStore.practiceQuizState
        signalrService.setPracticeState(classStore.roomName, {
            ...practiceState,
            [Session.quiz]: true
        })
    }

    function stopQuiz() {
        if (user.userRole !== UserRole.teacher) return;

        const practiceState = sessionStore.practiceQuizState
        signalrService.setPracticeState(classStore.roomName, {
            ...practiceState,
            [Session.quiz]: false
        })
    }

    function updateLectureQuestion(question: string) {
        if (user.userRole !== UserRole.teacher) return;
        if (!classStore.selectedClassDetails?.id) return;

        signalrService.updateLectureQuestion(classStore.roomName, classStore.selectedClassDetails.id, question)
    }

    function allowSpeaking(studentIds: number[], isFollowTeacher: true | null) {
        if (user.userRole !== UserRole.teacher) return;

        const usernames: string[] = studentIds.map(studentId => {
            const student = classStore.selectedClassDetails?.students?.find(student => student.id === studentId)
            return student?.username || ''
        }).filter(username => username)

        const vocabId: string = isFollowTeacher ? sessionStore.currentVocabId.toString() : '0'

        signalrService.selectStudentAnswer(classStore.roomName, vocabId, usernames)
    }

    function teacherChangeVocab() {
        if (!sessionStore.allowSpeakingStudentIds.length) return;
        if (!sessionStore.currentVocabId) return;

        const usernames: string[] = sessionStore.allowSpeakingStudentIds.map(studentId => {
            const student = classStore.selectedClassDetails?.students?.find(student => student.id === studentId)
            return student?.username || ''
        }).filter(username => username)

        signalrService.selectStudentAnswer(classStore.roomName, sessionStore.currentVocabId.toString(), usernames)
    }

    function quizSetting() {
        if (user.userRole !== UserRole.teacher) return;
        const config = quizStore.quizSetting
        signalrService.settingQuiz(classStore.roomName, config.showCorrectAnswer, config.showRanking, config.questionTime)
    }

    function quizStart() {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.startQuiz(classStore.roomName)
    }

    function quizFinished() {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.finishedQuiz(classStore.roomName)
    }
    function quizChangeQuestion(questionId: number, totalTime: number) {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.changeQuizQuestion(classStore.roomName, questionId, totalTime)
    }
    function finishedCurrentQuestion() {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.finishedCurrentQuestion(classStore.roomName)
    }
    function reviewCurrentQuestion() {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.reviewCurrentQuestion(classStore.roomName)
    }
    function answerQuestion(questionId: number, setId: number, type: AssessmentType, startTime: number, answers: string[]) {
        if (user.userRole !== UserRole.student) return;
        if (!questionId || !setId) return;

        const roomId = classStore.selectedClassDetails?.id
        if (!roomId) return;

        if (type === AssessmentType.QUIZ) {
            quizStore.isSubmitting = true
        }
        signalrService.answerQuestion(classStore.roomName, roomId, questionId, setId, type, answers, startTime)
    }
    function updateQuizState(state: QuizState) {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.updateQuizState(classStore.roomName, state)
    }

    function updateLectureSettings(studentViewAnswer: boolean) {
        if (user.userRole !== UserRole.teacher) return;
        signalrService.updateLectureSettings(classStore.roomName, studentViewAnswer ? 1 : 0)
    }

    function submitLecture(status: boolean = true) {
        lecture.isSubmitting = true
        signalrService.submitLecture(classStore.roomName, status)
    }

    function methodHandler(payload: SignalRPayload) {
        // TODO: check if isJoinRoom data is needed
        // if (user.username && payload.data.room.users) {
        //     if (payload.data.room.users[user.username]) {
        //         isJoinedRoom.value = true
        //     } else {
        //         isJoinedRoom.value = false
        //     }
        // }

        // Sentry.captureMessage(payload.action, {
        //     level: 'log',
        //     tags: {
        //         system: classStore.selectedClassDetails?.system,
        //         classId: classStore.selectedClassDetails?.id,
        //         role: user.userRole,
        //         actionType: 'receive'
        //     },
        //     extra: payload as any
        // })

        if (!actionHandler[payload.action]) {
            toast.error(lang.i18n.global.t(TEXT.no_signalr_action_hander + payload.action))
            return
        }

        // prevent message from other room
        if (payload.data?.room?.name && payload.data.room.name !== classStore.roomName) return;

        actionHandler[payload.action](payload)
    }

    const actionHandler = {
        [SignalRActionName.joined]: (payload: SignalRActionPayload) => {
            classStore.syncClassStudents(payload.data.room.users)
            classStore.syncClass(payload.data.room)
            classStore.syncGroupPoints(payload.data.room.groups)
            sessionStore.syncSession(payload.data.room)
            quizStore.syncQuiz(payload.data.room)
            lecture.syncLecture(payload.data.room)
        },
        [SignalRActionName.chooseGroup]: (payload: SignalRActionPayload) => {
            classStore.syncClassStudents(payload.data.room.users)
        },
        [SignalRActionName.changeGroup]: (payload: SignalRActionPayload) => {
            classStore.syncClassStudents(payload.data.room.users)
        },
        [SignalRActionName.askJoinRoom]: (payload: SignalRActionPayload) => {
            classStore.syncClass(payload.data.room)
        },
        [SignalRActionName.approveJoin]: (payload: SignalRActionPayload) => {
            if (user.userRole === UserRole.student) {
                toast.success(lang.i18n.global.t(TEXT.approve_join))
            }
            classStore.syncClass(payload.data.room)
        },
        [SignalRActionName.roomReady]: (payload: SignalRActionPayload) => {
            classStore.syncClass(payload.data.room)
            classStore.viewMode = ClassViewMode.teaching
        },
        [SignalRActionName.roomFinished]: () => {
            const loading = useLoadingStore()
            loading.isClassFinished = true

            if (user.userRole === UserRole.student) {
                const auth = useAuthStore()
                auth.logout()
                return
            }

            classStore.clearClassInfo()
        },
        [SignalRActionName.userLeftRoom]: (payload: SignalRActionPayload) => {
            if (payload.data.room.state === ClassStatus.finished) return;
            classStore.syncClassStudents(payload.data.room.users)
        },
        [SignalRActionName.userDisconnected]: (payload: SignalRActionPayload) => {
            // if (user.userRole === UserRole.teacher) {
            //     const student = classStore.selectedClassDetails?.students?.find(student => student.id === parseInt(payload.data.user?.id || '0'))
            //     if (student) {
            //         toast.warning(lang.i18n.global.t(TEXT.student_disconnected, { student: student.full_name }))
            //     }
            // }
            classStore.syncClassStudents(payload.data.room.users)
        },
        [SignalRActionName.requestRaiseHand]: (payload: SignalRActionPayload) => {
            classStore.syncClass(payload.data.room)
        },
        [SignalRActionName.raiseHandUpdate]: (payload: SignalRActionPayload) => {
            classStore.syncClass(payload.data.room)
        },
        [SignalRActionName.lockScreen]: (payload: SignalRActionPayload) => {
            classStore.syncClass(payload.data.room)
        },
        [SignalRActionName.rewardPoint]: (payload: SignalRActionPayload) => {
            classStore.syncClassStudents(payload.data.room.users)
        },
        [SignalRActionName.rewardPointGroup]: (payload: SignalRActionPayload) => {
            classStore.syncGroupPoints(payload.data.room.groups)
        },
        [SignalRActionName.controlScreen]: (payload: SignalRActionPayload) => {
            sessionStore.syncSession(payload.data.room)
        },
        [SignalRActionName.setPracticeState]: (payload: SignalRActionPayload) => {
            sessionStore.syncSession(payload.data.room)
        },
        [SignalRActionName.updateQuizUrl]: (payload: SignalRActionPayload) => {
            if (user.userRole === UserRole.teacher) {
                toast.success(lang.i18n.global.t(TEXT.quiz_updated))
            }
            sessionStore.syncClassContent(payload.data.content)
        },
        [SignalRActionName.updateLectureQuestion]: (payload: SignalRActionPayload) => {
            if (user.userRole === UserRole.teacher) {
                toast.success(lang.i18n.global.t(TEXT.lecture_updated))
            }
            sessionStore.syncClassContent(payload.data.content)
        },
        [SignalRActionName.selectStudentAnswer]: (payload: SignalRActionPayload) => {
            sessionStore.syncSession(payload.data.room)
        },
        [SignalRActionName.error]: (payload: SignalRPayload) => {
            // temp handle case student reconnect after class finished
            if (payload.message === 'Room finished') {
                const loading = useLoadingStore()
                loading.isClassFinished = true

                if (user.userRole === UserRole.student) {
                    const auth = useAuthStore()
                    auth.logout()
                    return
                }

                classStore.clearClassInfo()
                return
            }

            // temp handle case user not found
            if (payload.message === 'User not found') {
                window.location.reload()
            }

            toast.error(lang.i18n.global.t(payload.message))
        },
        [SignalRActionName.quizReady]: () => {
            if (!quizStore.quiz) return;
            quizChangeQuestion(quizStore.quiz.questions[0].id, quizStore.quiz.questions[0].questionTime)
        },
        [SignalRActionName.userAnswerQuestion]: (payload: SignalRActionPayload) => {
            classStore.syncClassStudents(payload.data.room.users)
        },
        [SignalRActionName.quizSetting]: (payload: SignalRActionPayload) => {
            quizStore.syncQuiz(payload.data.room)
        },
        [SignalRActionName.quizFinished]: (payload: SignalRActionPayload) => {
            quizStore.syncQuiz(payload.data.room)
        },
        [SignalRActionName.changeQuestion]: (payload: SignalRActionPayload) => {
            quizStore.isCountingDown = true
            quizStore.syncQuiz(payload.data.room)
        },
        [SignalRActionName.finishedCurrentQuestion]: (payload: SignalRActionPayload) => {
            quizStore.syncQuiz(payload.data.room)
        },
        [SignalRActionName.reviewCurrentQuestion]: (payload: SignalRActionPayload) => {
            quizStore.syncQuiz(payload.data.room)
        },
        [SignalRActionName.updateQuizState]: (payload: SignalRActionPayload) => {
            quizStore.syncQuiz(payload.data.room)
        },
        [SignalRActionName.lectureSetting]: (payload: SignalRActionPayload) => {
            lecture.syncLecture(payload.data.room)
        },
        [SignalRActionName.lectureSubmit]: (payload: SignalRActionPayload) => {
            classStore.syncClassStudents(payload.data.room.users)
        }
    }

    return {
        isConnected,
        // isJoinedRoom,
        connect,
        disconnect,
        onEventHandler,
        offEventHandler,
        getCurrentQuestion,
        joinRoom,
        leaveRoom,
        studentChooseGroup,
        teacherChangeGroup,
        studentAskJoinRoom,
        startClassRoom,
        endClassRoom,
        allowRaiseHand,
        disableRaiseHand,
        removeRequestRaiseHand,
        raiseHand,
        removeRaiseHand,
        lockScreen,
        rewardPointStudent,
        rewardPointGroup,
        forceScreen,
        unForceScreen,
        startPractice,
        updateQuizUrl,
        startQuiz,
        stopQuiz,
        updateLectureQuestion,
        allowSpeaking,
        teacherChangeVocab,
        quizSetting,
        quizStart,
        quizFinished,
        quizChangeQuestion,
        finishedCurrentQuestion,
        reviewCurrentQuestion,
        answerQuestion,
        updateQuizState,
        updateLectureSettings,
        submitLecture
    }
})